<template>
  <div class="mail-types-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">أنواع المراسلات</h1>
          <p class="text-body-1 text-grey">إدارة أنواع المراسلات في النظام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة أنواع المراسلات</h2>
            <v-btn
            v-if="hasPermission('admin.mail-types.store')"
              v-action="{ module: 'mail-types', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddMailTypeDialog"
            >
              إضافة نوع جديد
            </v-btn>
          </div>

          <v-data-table
            :headers="mailTypeHeaders"
            :items="mailTypes"
            :search="mailTypeSearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="mailTypeSearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في أنواع المراسلات"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'error'" size="small" variant="tonal">
                {{ item.active ? 'نشط' : 'غير نشط' }}
              </v-chip>
            </template>

            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.mail-types.update')"
                v-action="{ module: 'mail-types', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editMailType(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.mail-types.destroy')"
                v-action="{ module: 'mail-types', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'mailType')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Mail Type Dialog -->
    <v-dialog v-model="showMailTypeDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingMailType ? 'تعديل نوع المراسل' : 'إضافة نوع مراسل جديد' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="mailTypeForm" v-model="valid" @submit.prevent="saveMailType">
            <v-text-field
              v-model="mailTypeForm.name"
              label="اسم النوع"
              variant="outlined"
              required
              :rules="[v => !!v || 'اسم النوع مطلوب']"
              class="mb-4"
              placeholder="عام، إداري، مالي..."
            ></v-text-field>
            
            <v-textarea
              v-model="mailTypeForm.desc"
              label="الوصف"
              variant="outlined"
              rows="3"
              class="mb-4"
            ></v-textarea>
            
            <v-switch
              v-model="mailTypeForm.active"
              label="نشط"
              color="success"
              class="mb-4"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showMailTypeDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="saveMailType">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف نوع المراسل؟
          <br><strong>{{ itemToDelete?.name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import mailTypeService from '@/services/mailTypeService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'MailTypesView',
  data() {
    return {
      loading: false,
      valid: false,
      mailTypeSearch: '',
      showMailTypeDialog: false,
      editingMailType: null,
      mailTypes: [],
      mailTypeForm: {
        name: '',
        desc: '',
        active: true
      },
      mailTypeHeaders: [
        { title: 'اسم النوع', key: 'name', align: 'start' },
        { title: 'الوصف', key: 'desc', align: 'start' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'تاريخ الإنشاء', key: 'created_at', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: ''
    }
  },
  async mounted() {
    await this.loadMailTypes()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    
    async loadMailTypes() {
      this.loading = true
      try {
        const response = await mailTypeService.getMailTypes()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.mailTypes = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.mailTypes = response.data
        } else {
          this.mailTypes = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب أنواع المراسلات'
        })
      } finally {
        this.loading = false
      }
    },

    openAddMailTypeDialog() {
      this.editingMailType = null
      this.mailTypeForm = {
        name: '',
        desc: '',
        active: true
      }
      this.showMailTypeDialog = true
    },

    editMailType(mailType) {
      this.editingMailType = mailType
      this.mailTypeForm = {
        name: mailType.name,
        desc: mailType.desc,
        active: Boolean(mailType.active)
      }
      this.showMailTypeDialog = true
    },

    async saveMailType() {
      if (!this.$refs.mailTypeForm.validate()) return

      this.loading = true
      try {
        if (this.editingMailType) {
          await mailTypeService.updateMailType(this.editingMailType.id, this.mailTypeForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث نوع المراسل بنجاح'
          })
        } else {
          await mailTypeService.createMailType(this.mailTypeForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة نوع المراسل بنجاح'
          })
        }
        
        this.showMailTypeDialog = false
        await this.loadMailTypes()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ نوع المراسل'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await mailTypeService.deleteMailType(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف نوع المراسل بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadMailTypes()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف نوع المراسل'
        })
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      if (!date) return 'غير محدد'
      return new Date(date).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.mail-types-settings {
  min-height: 100vh;
}
</style> 